// Systeemstatus: een plek waar je ziet of de OS zelf gezond is.
// Geplande taken (laatste run en fouten), live diensten, koppelingen, AI-budget en een back-up die je zelf kunt downloaden.
// Een stille fout, zoals een stukke sleutel of een cron die niet meer draait, wordt hier zichtbaar.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase";
import { C, eur, datumNl, lees, tabelOntbreekt, Kop, Stat, Chip, Melding } from "./gedeeld";
import { laadTellers } from "./actieTellers";
import { leesModusActief } from "./leesModus";

const MODULE_NAAM = { goedkeuringen: "Goedkeuringen", growth: "Growth", operatie: "Operatie", klantenservice: "Klantenservice", crm: "CRM", facturatie: "Offertes & Facturen", taken: "Taken", systeem: "Systeemstatus" };

// Tabellen in de back-up. app_settings en andere tabellen met geheimen staan er bewust niet in.
const BACKUP_TABELLEN = [
  "leads", "activities", "tasks", "events", "meetings", "documents", "bugs", "services", "pipeline", "launch_modules",
  "crm_services", "crm_offertes", "klant_diensten", "klantenservice_tickets", "admin_docs", "admin_transactions",
  "ledger_accounts", "journal_entries", "growth_reviews", "website_aanvragen", "nummerreeks",
];

const uurGeleden = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 3600000 : null);
const leesbaar = (u) => (u == null ? "nooit" : u < 1 ? Math.round(u * 60) + " min geleden" : u < 48 ? Math.round(u) + " uur geleden" : Math.round(u / 24) + " dagen geleden");

export default function Systeemstatus({ user }) {
  const [cron, setCron] = useState([]);
  const [cronFout, setCronFout] = useState(null);
  const [uptime, setUptime] = useState([]);
  const [meta, setMeta] = useState([]);
  const [budget, setBudget] = useState(null);
  const [backup, setBackup] = useState({ laatste: null, bezig: false, voortgang: "", fouten: [] });
  const [laden, setLaden] = useState(true);
  const [acties, setActies] = useState({});
  const [koppelingen, setKoppelingen] = useState(null); // null = nog niet beschikbaar (migratie systeem_alarm.sql)

  const laad = useCallback(async () => {
    const [c, u, m, b, s] = await Promise.all([
      supabase.rpc("systeem_cron_status"),
      lees(supabase.from("uptime_checks").select("dienst,ok,http_status,latency_ms,detail,checked_at").gte("checked_at", new Date(Date.now() - 7 * 86400000).toISOString()).order("checked_at", { ascending: false }).limit(500)),
      lees(supabase.from("meta_ads_accounts").select("label,token_status,last_sync_at")),
      lees(supabase.from("claude_spend_ledger").select("day,spent_eur,cap_eur").eq("day", new Date().toISOString().slice(0, 10))),
      lees(supabase.from("app_settings").select("value").eq("key", "laatste_backup_download")),
    ]);
    laadTellers(supabase).then(setActies).catch(() => {});
    supabase.rpc("systeem_koppelingen").then((r) => setKoppelingen(r && !r.error && Array.isArray(r.data) ? r.data : null), () => setKoppelingen(null));
    setCron(c.data || []); setCronFout(c.error || null);
    setUptime(u.data); setMeta(m.data); setBudget(b.data[0] || null);
    setBackup((p) => ({ ...p, laatste: (s.data[0] && s.data[0].value) || null }));
    setLaden(false);
  }, []);

  useEffect(() => { laad(); }, [laad]);

  // Per live dienst alleen de laatste meting
  const perDienst = [];
  const gezien = new Set();
  uptime.forEach((x) => { if (!gezien.has(x.dienst)) { gezien.add(x.dienst); perDienst.push(x); } });

  const uptimePct = (dienst) => {
    const r = uptime.filter((x) => x.dienst === dienst);
    return r.length ? Math.round((r.filter((x) => x.ok).length / r.length) * 1000) / 10 : null;
  };

  async function maakBackup() {
    setBackup((p) => ({ ...p, bezig: true, voortgang: "Start...", fouten: [] }));
    const uit = { gemaakt_op: new Date().toISOString(), gemaakt_door: (user && user.email) || "", tabellen: {} };
    const fouten = [];
    const controle = [];
    for (const t of BACKUP_TABELLEN) {
      setBackup((p) => ({ ...p, voortgang: t }));
      const rijen = [];
      let van = 0;
      for (;;) {
        const { data, error } = await supabase.from(t).select("*").range(van, van + 999);
        if (error) { if (!tabelOntbreekt(error)) fouten.push(t + ": " + error.message); break; }
        rijen.push(...data);
        if (data.length < 1000) break;
        van += 1000;
      }
      uit.tabellen[t] = rijen;
      // Herstelcontrole: het aantal rijen in het bestand moet gelijk zijn aan het aantal in de database.
      const { count } = await supabase.from(t).select("*", { count: "exact", head: true });
      if (count != null && count !== rijen.length) controle.push(t + " (" + rijen.length + " van " + count + ")");
    }
    if (controle.length) fouten.push("aantal rijen klopt niet: " + controle.join(", "));
    const blob = new Blob([JSON.stringify(uit, null, 1)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "dif3r3nt-os-backup-" + new Date().toISOString().slice(0, 10) + ".json";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    const nu = new Date().toISOString();
    if (!fouten.length) await supabase.from("app_settings").upsert({ key: "laatste_backup_download", value: nu, updated_by: (user && user.email) || "furgel", updated_at: nu });
    setBackup({ laatste: fouten.length ? backup.laatste : nu, bezig: false, voortgang: "", fouten });
  }

  const cronProblemen = cron.filter((c) => c.active && (c.laatste_status === "failed" || Number(c.fouten_24u) > 0));
  const dienstenDown = perDienst.filter((x) => !x.ok);
  const metaProblemen = meta.filter((x) => x.token_status && x.token_status !== "ok");
  const koppelingStuk = (koppelingen || []).filter((k) => k.status === "stuk");
  const backupLeeftijd = uurGeleden(backup.laatste);
  // Gast met leesrechten: geplande taken, koppelingen en back-up zijn alleen voor beheerders. Dat zeggen we, in plaats van een migratie te vragen.
  const gast = leesModusActief();
  const alles = !cronProblemen.length && !dienstenDown.length && !metaProblemen.length && !koppelingStuk.length;

  return (
    <div>
      <Kop titel="Systeemstatus" sub="Is de OS zelf gezond? Geplande taken, diensten, koppelingen, budget en back-up." rechts={<button className="btn bg" onClick={laad}>Vernieuwen</button>} />
      {laden && <p style={{ color: C.muted, fontSize: 13 }}>Laden...</p>}
      {!laden && alles && !cronFout && <Melding type="ok">Alles draait: geen geplande taken met fouten, geen diensten met storing en alle gekoppelde accounts zijn in orde.</Melding>}
      {!laden && !alles && <Melding type="fout"><strong>Er is iets stuk.</strong> {cronProblemen.length ? cronProblemen.length + " geplande taken met fouten. " : ""}{dienstenDown.length ? dienstenDown.length + " diensten niet bereikbaar. " : ""}{metaProblemen.length ? "Een advertentie-account heeft een token dat niet meer werkt. " : ""}{koppelingStuk.length ? koppelingStuk.map((k) => k.naam).join(", ") + " werkt niet." : ""}</Melding>}
      {cronFout && !gast && <Melding type="let-op">De status van geplande taken kan niet worden gelezen ({cronFout.message}). Voer <code>supabase/migrations/os_optimalisatie.sql</code> uit om dit blok te activeren.</Melding>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, marginBottom: 16 }}>
        {!gast && <Stat label="Geplande taken" waarde={cron.filter((c) => c.active).length} sub={cronProblemen.length ? cronProblemen.length + " met fouten" : "geen fouten"} kleur={cronProblemen.length ? C.red : C.green} />}
        <Stat label="Diensten bereikbaar" waarde={perDienst.length ? perDienst.length - dienstenDown.length + " / " + perDienst.length : "geen metingen"} kleur={dienstenDown.length ? C.red : C.green} />
        <Stat label="AI-budget vandaag" waarde={budget ? eur(budget.spent_eur) : "€ 0,00"} sub={budget ? "van " + eur(budget.cap_eur) : "geen verbruik"} />
        {!gast && <Stat label="Laatste back-up" waarde={backup.laatste ? leesbaar(backupLeeftijd) : "nooit"} kleur={backupLeeftijd == null || backupLeeftijd > 24 * 14 ? C.red : backupLeeftijd > 24 * 7 ? C.yellow : C.green} />}
      </div>

      {Object.keys(acties).length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <p className="lbl" style={{ marginBottom: 8 }}>Wacht op jou</p>
          {Object.entries(acties).map(([id, a]) => (
            <div key={id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
              <span><strong>{MODULE_NAAM[id] || id}</strong> <span style={{ color: C.muted }}>{a.tip}</span></span>
              <Chip tekst={String(a.n)} kleur={a.kleur === "rood" ? C.red : C.yellow} />
            </div>
          ))}
        </div>
      )}

      {!gast && <div className="card" style={{ marginBottom: 12 }}>
        <p className="lbl" style={{ marginBottom: 8 }}>Back-up</p>
        <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px", lineHeight: 1.6 }}>
          Download alle klant-, financiele en werkgegevens als een JSON-bestand. Bewaar het op een veilige plek buiten de OS, want het bevat persoonsgegevens.
          Het bestand bevat geen wachtwoorden of sleutels. Doe dit wekelijks. Een back-up die je nooit hebt teruggezet is nog geen bewijs dat herstel werkt: laat in Supabase ook de dagelijkse back-ups (Database, Backups) aanstaan.
        </p>
        <button className="btn bp" disabled={backup.bezig} onClick={maakBackup}>{backup.bezig ? "Bezig met " + backup.voortgang + "..." : "Download back-up"}</button>
        {backup.fouten.length > 0 && <div style={{ marginTop: 10 }}><Melding type="let-op">De back-up is gemaakt, maar niet alles klopt:{backup.fouten.join("; ")}. De datum van de laatste back-up is niet bijgewerkt.</Melding></div>}
      </div>}

      {!gast && <div className="card" style={{ marginBottom: 12, padding: 0 }}>
        <p className="lbl" style={{ padding: "14px 16px 6px" }}>Geplande taken</p>
        {cron.length === 0 && !cronFout && <p style={{ padding: "0 16px 14px", fontSize: 12, color: C.dim }}>Geen geplande taken gevonden.</p>}
        {cron.map((c) => {
          const u = uurGeleden(c.laatste_run);
          const stuk = c.active && (c.laatste_status === "failed" || Number(c.fouten_24u) > 0);
          return (
            <div key={c.jobname} className="row" style={{ gridTemplateColumns: "1.3fr 1fr 1.3fr", gap: 10, fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>{c.jobname}</span>
              <span style={{ color: C.muted }}>{c.laatste_run ? leesbaar(u) : "nog niet gedraaid"}</span>
              <span style={{ textAlign: "right" }}>
                {!c.active ? <Chip tekst="uitgeschakeld" /> : stuk ? <Chip tekst={c.fouten_24u + " fouten in 24 uur"} kleur={C.red} /> : <Chip tekst="ok" kleur={C.green} />}
                {stuk && c.laatste_melding && <span style={{ display: "block", color: C.red, fontSize: 11, marginTop: 3 }}>{c.laatste_melding}</span>}
              </span>
            </div>
          );
        })}
      </div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 12 }}>
        <div className="card">
          <p className="lbl" style={{ marginBottom: 8 }}>Live diensten (laatste meting)</p>
          {perDienst.length === 0 && <p style={{ fontSize: 12, color: C.dim, margin: 0 }}>Nog geen metingen.</p>}
          {perDienst.map((x) => (
            <div key={x.dienst} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
              <span>{x.dienst} <span style={{ color: C.muted }}>{leesbaar(uurGeleden(x.checked_at))}{uptimePct(x.dienst) != null ? " · " + uptimePct(x.dienst) + "% in 7 dagen" : ""}</span></span>
              <span>{x.ok ? <Chip tekst={(x.latency_ms || 0) + " ms"} kleur={C.green} /> : <Chip tekst={"storing " + (x.http_status || "")} kleur={C.red} />}</span>
            </div>
          ))}
        </div>
        {!gast && <div className="card">
          <p className="lbl" style={{ marginBottom: 8 }}>Koppelingen</p>
          {meta.length === 0 && <p style={{ fontSize: 12, color: C.dim, margin: 0 }}>Geen advertentie-accounts gekoppeld.</p>}
          {meta.map((m) => (
            <div key={m.label} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
              <span>Meta Ads: {m.label} {m.last_sync_at && <span style={{ color: C.muted }}>sync {datumNl(m.last_sync_at)}</span>}</span>
              <Chip tekst={m.token_status || "onbekend"} kleur={m.token_status === "ok" ? C.green : C.red} />
            </div>
          ))}
          {koppelingen && koppelingen.map((k) => (
            <div key={k.naam} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
              <span>{k.naam} <span style={{ color: C.muted }}>{k.detail}{k.laatste_ok ? ", laatst " + datumNl(k.laatste_ok) : ""}</span></span>
              <Chip tekst={k.status} kleur={k.status === "ok" ? C.green : k.status === "stuk" ? C.red : C.yellow} />
            </div>
          ))}
          {!koppelingen && <p style={{ fontSize: 11, color: C.muted, margin: "10px 0 0", lineHeight: 1.5 }}>Koppelingen per stuk (website, Mollie, meldingen) verschijnen hier na het uitvoeren van <code>supabase/migrations/systeem_alarm.sql</code>. Die zet ook de pushmelding aan als er iets stuk gaat.</p>}
        </div>}
      </div>
    </div>
  );
}
